import {
  createBrowserRouter,
  createRoutesFromElements,
  Link,
  Navigate,
  Route,
  Outlet,
} from 'react-router-dom';
import { Box, Heading } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { IntercomProvider } from 'react-use-intercom';
import { lazy } from 'react';
import { LazyOutlet } from 'design-system/atoms/spinner-suspense';
import {
  ForgotPassword,
  Login,
  ResetPassword,
  VerifyMFA,
} from '../../components/Auth';
import {
  pathCookies,
  pathDashboard,
  pathForbidden,
  pathForgotPassword,
  pathLogin,
  pathLogout,
  pathMfaLogin,
  pathOnboarding,
  pathResetPassword,
  pathRouteStatus,
  pathUnsubscribe,
  pathRoutePollStatus,
  pathImpactAssessmentRequest,
  pathContractorOnboarding,
  pathContractorOnboardingExistingUser,
  pathContractorOnboardingExistingUserCreateOrg,
} from '../../constants/path';

import ErrorPage from '../../components/ErrorPage';
import Logout from '../../components/Auth/Logout';
import RouteErrorBoundary from '../../components/RouteErrorBoundary';
import { envConfig, getEnvironment } from '../../config/envConfig';
import { RouteDetails } from '../../components/LiveNetworkMonitoring/RouteDetails';
import { WithProfiler } from './with-profiler';
import {
  pathOrg,
  pathManagement,
  pathSiteMonitoringPlanning,
  pathSiteMonitoringPerformance,
  pathSiteMonitoringEdit,
  pathSiteMonitoringVms,
  pathManagementProject,
  pathReporting,
  pathOrgSettings,
  pathOrgTeamSettings,
  pathOrgUserSettings,
  pathOperationsHubRoute,
  pathOperationsHub,
  pathSiteMonitoring,
  pathOrgTeamFragmentSettings,
  pathSiteMonitoringIndex,
  pathOrgSettingsAccount,
  pathSiteMonitoringVmsBoard,
  pathSiteMonitoringAlerts,
  pathOrgDataSourcesSettings,
  pathOrgContractorsSettings,
  pathOrgUserAccount,
  pathOrgUserPreference,
  pathManagementScheduling,
  pathManagementSchedulingCrew,
} from '../../router/utils/path';
import { ConfiguredSWRWrapper } from '../../config/swr';
import PageDashboard from '../../router/pages/dashboard';
import PagePublicTransportSegment from '../../router/pages/org/site-monitoring/site/current-performance/public-transport/segment';
import ContextCharts from '../../router/context/ContextCharts';
import ContractorCreateOrg from '../../components/Onboarding/ContractorCreateOrg';
import ContextContractor from '../../router/context/ContextContractor';
import LayoutUserAccount from '../../router/layout/LayoutUserAccount';
import OnBoardExistContractorUser from '../../components/Onboarding/OnboardExistContractorUser';
import OnboardNewOrgExistContractorUser from '../../components/Onboarding/OnboardNewOrgExistContractorUser';
import ContextProject from '../../router/context/ContextProject';
import ContextSite from '../../router/context/ContextSite';
import ContextVMS from '../../router/context/ContextVMS';
import ContextRoute from '../../router/context/ContextRoute';
import ContextAuth from '../../router/context/ContextAuth';
import ContextOrg from '../../router/context/ContextOrg';

const LayoutSite = lazy(() => import('../../router/layout/LayoutSite'));
const LayoutSitePerformance = lazy(
  () => import('../../router/layout/LayoutSitePerformance')
);
const LayoutManagement = lazy(
  () => import('../../router/layout/LayoutManagement')
);
const LayoutManagementScheduling = lazy(
  () => import('../../router/layout/LayoutManagementScheduling')
);
const LayoutSettings = lazy(() => import('../../router/layout/LayoutSettings'));
const LayoutSiteMonitoringIndex = lazy(
  () => import('../../router/layout/LayoutSiteMonitoringIndex')
);
const LayoutHeader = lazy(() => import('../../components/LayoutHeader'));
const LayoutLiveNetworkMonitoring = lazy(
  () => import('../../router/layout/LayoutLiveNetworkMonitoring')
);
const LayoutOrg = lazy(() => import('../../router/layout/LayoutOrg'));
const RedirectLegacySiteUrl = lazy(() => import('./RedirectSiteUrl'));
const PageOrg = lazy(() => import('../../router/pages/org'));
const PageLiveNetwork = lazy(
  () => import('../../router/pages/org/live-network')
);
const PageManagement = lazy(() => import('../../router/pages/org/management'));

const PageReporting = lazy(() => import('../../router/pages/org/reporting'));
const PageSettings = lazy(() => import('../../router/pages/org/settings'));
const PageSitePlanning = lazy(
  () => import('../../router/pages/org/site-monitoring/site/site-planning')
);
const PageCurrentPerformance = lazy(
  () =>
    import('../../router/pages/org/site-monitoring/site/current-performance')
);
const PagePublicTransport = lazy(
  () =>
    import(
      '../../router/pages/org/site-monitoring/site/current-performance/public-transport'
    )
);
const PageSmartVms = lazy(
  () => import('../../router/pages/org/site-monitoring/site/smart-vms')
);
const PageVmsBoard = lazy(
  () => import('../../router/pages/org/site-monitoring/site/smart-vms/board')
);
const PageEditSite = lazy(
  () => import('../../router/pages/org/site-monitoring/site/edit-site')
);
const PageSiteAlerts = lazy(
  () => import('../../router/pages/org/site-monitoring/site/site-alerts')
);
const PageProject = lazy(
  () => import('../../router/pages/org/management/project')
);
const PageSchedulingProject = lazy(
  () => import('../../router/pages/org/management/scheduling')
);
const PageSchedulingCrew = lazy(
  () => import('../../router/pages/org/management/scheduling/crew')
);

const PageSiteMonitoring = lazy(
  () => import('../../router/pages/org/site-monitoring')
);
const PageUserSettings = lazy(
  () => import('../../router/pages/org/settings/PageUserSettings')
);
const PageOrgUserAccount = lazy(
  () => import('../../router/pages/org/user-account')
);
const PageUserPreference = lazy(
  () => import('../../router/pages/org/user-account/preference')
);
const PageDataSources = lazy(
  () => import('../../router/pages/org/settings/PageDataSources')
);
const PageTeamSettings = lazy(
  () => import('../../router/pages/org/settings/PageTeamSettings')
);
const PageContractorsSettings = lazy(
  () => import('../../router/pages/org/settings/PageContractorsSettings')
);
const PageAccountSettings = lazy(
  () => import('../../router/pages/org/settings/PageAccountSettings')
);
const PageSiteMonitoringSite = lazy(
  () => import('../../router/pages/org/site-monitoring/site')
);

const Onboarding = lazy(() => import('../../components/Onboarding'));
const Unsubscribe = lazy(() => import('../../components/Unsubscribe'));
const ImpactAssessmentRequest = lazy(
  () => import('../../components/ImpactAssessmentRequest')
);
const RoutePollingStatus = lazy(
  () => import('../../components/RoutePollingStatus')
);

export default createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        getEnvironment() !== 'production' ? (
          <WithProfiler profilerId="root" />
        ) : (
          <Outlet />
        )
      }
    >
      <Route element={<AppWrappers />} errorElement={<RouteErrorBoundary />}>
        <Route path="/" element={<Navigate to={pathDashboard()} replace />} />
        <Route
          path={pathContractorOnboarding()}
          element={<ContractorCreateOrg />}
        />
        <Route
          path={pathCookies()}
          element={
            <ErrorPage
              text="Please enable cookies to view this website"
              noLink
            />
          }
        />
        <Route
          path={pathForbidden()}
          element={<ErrorPage text="403 Forbidden" />}
        />

        <Route path={pathLogin()} element={<Login />} />

        <Route element={<ContextAuth userRequired={false} />}>
          <Route path={pathUnsubscribe()} element={<Unsubscribe />} />
          <Route
            path={pathRoutePollStatus()}
            element={<RoutePollingStatus />}
          />
          <Route path={pathForgotPassword()} element={<ForgotPassword />} />
          <Route path={pathResetPassword()} element={<ResetPassword />} />
        </Route>

        <Route element={<ContextAuth onboarding />}>
          <Route path={pathOnboarding()} element={<Onboarding />} />
        </Route>

        <Route element={<ContextAuth />}>
          <Route
            path={pathContractorOnboardingExistingUserCreateOrg()}
            element={<OnboardNewOrgExistContractorUser />}
          />
          <Route
            path={pathContractorOnboardingExistingUser()}
            element={<OnBoardExistContractorUser />}
          />
          <Route path={pathMfaLogin()} element={<VerifyMFA />} />

          <Route element={<ContextOrg />}>
            <Route element={<LayoutOrg />} path={pathOrg()}>
              <Route index element={<PageOrg />} />
              <Route element={<ContextProject />}>
                <Route element={<LayoutManagement />}>
                  <Route path={pathManagement()} element={<PageManagement />} />
                  <Route
                    path={pathManagementProject()}
                    element={<PageProject />}
                  />
                  <Route
                    path={pathManagementScheduling()}
                    element={<LayoutManagementScheduling />}
                  >
                    <Route index element={<PageSchedulingProject />} />
                    <Route
                      path={pathManagementSchedulingCrew()}
                      element={<PageSchedulingCrew />}
                    />
                  </Route>
                </Route>
              </Route>
              <Route element={<ContextRoute />}>
                <Route element={<ContextCharts />}>
                  <Route element={<LayoutLiveNetworkMonitoring />}>
                    <Route
                      path={pathOperationsHub()}
                      element={<PageLiveNetwork />}
                    />
                    <Route
                      path={pathOperationsHubRoute()}
                      element={<RouteDetails />}
                    />
                  </Route>
                </Route>
              </Route>
              <Route element={<LayoutSiteMonitoringIndex />}>
                <Route
                  path={pathSiteMonitoringIndex()}
                  element={<PageSiteMonitoring />}
                />
              </Route>
              <Route element={<ContextSite />}>
                <Route element={<LayoutSite />} path={pathSiteMonitoring()}>
                  <Route index element={<PageSiteMonitoringSite />} />
                  <Route
                    path={pathSiteMonitoringPlanning()}
                    element={<PageSitePlanning />}
                  />
                  <Route
                    path={`${pathSiteMonitoringPerformance()}/public-transport/segment`}
                    element={<PagePublicTransportSegment />}
                  />
                  <Route element={<ContextCharts />}>
                    <Route
                      path={pathSiteMonitoringPerformance()}
                      element={<LayoutSitePerformance />}
                    >
                      <Route index element={<PageCurrentPerformance />} />
                      <Route
                        path={`${pathSiteMonitoringPerformance()}/public-transport`}
                        element={<PagePublicTransport />}
                      />
                    </Route>
                  </Route>
                  <Route
                    path={pathSiteMonitoringEdit()}
                    element={<PageEditSite />}
                  />
                  <Route
                    path={pathSiteMonitoringAlerts()}
                    element={<PageSiteAlerts />}
                  />
                  <Route
                    path={pathSiteMonitoringVms()}
                    element={<ContextVMS />}
                  >
                    <Route index element={<PageSmartVms />} />
                    <Route
                      path={pathSiteMonitoringVmsBoard()}
                      element={<PageVmsBoard />}
                    />
                  </Route>
                </Route>
              </Route>
              <Route
                element={
                  <LayoutHeader
                    breadcrumbs={[
                      {
                        label: (
                          <Box data-testid="report-header">
                            <FormattedMessage
                              defaultMessage="Reporting"
                              id="reFEEC"
                            />
                          </Box>
                        ),
                      },
                    ]}
                  >
                    <LazyOutlet />
                  </LayoutHeader>
                }
              >
                <Route path={pathReporting()} element={<PageReporting />} />
              </Route>
              <Route
                path={pathOrgUserAccount()}
                element={<LayoutUserAccount />}
              >
                <Route index element={<PageOrgUserAccount />} />
                <Route
                  path={pathOrgUserPreference()}
                  element={<PageUserPreference />}
                />
              </Route>
              <Route element={<LayoutSettings />}>
                <Route path={pathOrgSettings()} element={<PageSettings />} />
                <Route
                  path={pathOrgSettingsAccount()}
                  element={<PageAccountSettings />}
                />
                <Route
                  path={pathOrgTeamSettings()}
                  element={<PageTeamSettings />}
                />
                <Route
                  path={pathOrgTeamFragmentSettings()}
                  element={<PageTeamSettings />}
                />
                <Route element={<ContextContractor />}>
                  <Route
                    path={pathOrgContractorsSettings()}
                    element={<PageContractorsSettings />}
                  />
                </Route>
                {/* TODO [FEATURE_PROFILE_EDITING]: Remove this route on feature cleanup */}
                <Route
                  path={pathOrgUserSettings()}
                  element={<PageUserSettings />}
                />
                <Route
                  path={pathOrgDataSourcesSettings()}
                  element={<PageDataSources />}
                />
              </Route>
            </Route>
          </Route>

          <Route path={pathLogout()} element={<Logout />} />
          <Route path={pathRouteStatus()} element={<RoutePollingStatus />} />
          <Route
            path={pathImpactAssessmentRequest()}
            Component={ImpactAssessmentRequest}
          />

          <Route path={pathDashboard()} element={<PageDashboard />} />
          <Route path="/sites/*" element={<RedirectLegacySiteUrl />} />

          <Route
            path={'/*'}
            element={
              <Box textAlign="center" px={12} py={20}>
                <Heading>
                  <FormattedMessage
                    defaultMessage="404 not found"
                    id="Cu34ak"
                    description="Page not found message"
                  />
                </Heading>
                <Link to="/">
                  <FormattedMessage
                    defaultMessage="Go to homepage"
                    id="bkWXbu"
                    description="link to homepage from error page"
                  />
                </Link>
              </Box>
            }
          />
        </Route>
      </Route>
    </Route>
  )
);

function AppWrappers() {
  return (
    <ConfiguredSWRWrapper>
      <IntercomProvider appId={envConfig.INTERCOM_APP}>
        <LazyOutlet />
      </IntercomProvider>
    </ConfiguredSWRWrapper>
  );
}
